/**
 * ErpPortalLayout — Portal del colaborador para usuarios del ERP (Caso B).
 *
 * A diferencia de PortalLayout (Caso A, autenticación propia del portal), este usa
 * la SESIÓN ERP ya autenticada (authStore) — sin re-login. Se abre en una pestaña
 * aparte desde el menú de usuario del ERP, con su propio header + tabs y SIN el
 * sidebar del ERP, para dar la experiencia limpia del portal del colaborador.
 */
import { Navigate, Outlet, Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { EnvBanner } from '@/components/EnvBanner'
import { PortalNav } from '../components/PortalNav'
import { useAuthStore } from '@/store/authStore'
import { useMe } from '@/modules/auth/hooks/useAuth'
import { usePortalMe } from '../hooks/useEmployeePortal'
import { Avatar } from '@/components/ds/Avatar'
import { InterruptorMontos } from '@/components/ds/InterruptorMontos'

function PortalHeader() {
  const { user } = useAuthStore()
  const name = user ? (user.full_name || user.email) : ''
  // La foto sale de `me/`, que responde a los dos casos de autenticación.
  const { data: me } = usePortalMe()
  return (
    <header className="cl-topbar border-b border-divider px-6 py-3 flex items-center justify-between shrink-0">
      <div className="flex items-center gap-3">
        <img src="/clobi-logo-light-compacto.svg" alt="Clobi" className="h-7 dark:hidden" />
        <img src="/clobi-logo-white-compacto.svg" alt="Clobi" className="h-7 hidden dark:block" />
        <span className="text-sm font-medium text-content-muted hidden sm:inline">
          Portal del Colaborador
        </span>
      </div>
      <div className="flex items-center gap-4">
        {/* Montos ocultos por defecto; un solo interruptor para todo el portal (plan, D5). */}
        <InterruptorMontos />
        {name && (
          <span className="flex items-center gap-2 text-sm text-content-muted">
            <Avatar nombre={me?.full_name || name} fotoUrl={me?.photo_url} tamano="sm" decorativa />
            <span className="hidden sm:inline">Hola, <strong className="text-content">{name}</strong></span>
          </span>
        )}
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 text-sm text-content-muted hover:text-content transition-colors"
        >
          <ArrowLeft size={15} aria-hidden="true" />
          <span className="hidden sm:inline">Volver al sistema</span>
        </Link>
      </div>
    </header>
  )
}

export default function ErpPortalLayout() {
  const { employeeRecord } = useAuthStore()
  // ⚠️ `employeeRecord` lo carga `useMe()`, que hasta #1656 solo se llamaba en el
  // layout del ERP. Recién ingresado y entrando DIRECTO al portal —el enlace del
  // aviso de firma— el store estaba vacío y esto mandaba al dashboard a quien sí
  // tenía ficha. Se pide acá y se espera la respuesta antes de decidir.
  const { isLoading } = useMe()

  // El portal Caso B requiere una ficha de empleado vinculada al usuario ERP.
  // Sin ella no hay datos que mostrar → volver al sistema.
  if (!employeeRecord) {
    if (isLoading) {
      return (
        <div className="h-screen flex items-center justify-center bg-canvas">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )
    }
    return <Navigate to="/dashboard" replace />
  }

  return (
    <div className="min-h-screen bg-canvas flex flex-col">
      <EnvBanner />
      <PortalHeader />
      <PortalNav />
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  )
}
